import React, { useCallback } from 'react';
import { Route } from 'react-router-dom';

import SectionAwareRoute from '../../components/route/SectionAwareRoute';
import Paths from '../../types/Paths';
import { CategoryPage, MobileCategoryPage } from './loadableCategoryPage';
import { MobileProductPage, ProductPage } from './loadableProductPage';
import {
  NewProductsPage,
  OnSalePage,
  PopularProductsPage,
  RecommendedProductsPage,
  SoldProductsPage,
} from './loadableProductListPages';
import { VehiclePartSearchPage } from './loadableVehiclePartSearchPage';

const createSectionRoute = (path, component) => {
  return {
    path,
    component,
  };
};

/**
 * @param {Object} props
 * @param {ConfigStore} props.configStore
 * @param {UIStore} props.uiStore
 * @param {Section} props.section
 * @param {string} props.match
 * @param {ReactNode} props.overrideComponent
 * @returns {Route[]}
 */
const useSectionDependantRoutes = ({
  configStore,
  uiStore,
  section,
  match,
  overrideComponent,
}) => {
  const getSectionRoutes = useCallback((isMobile) => {
    const productPageComponent = isMobile ? (
      <MobileProductPage />
    ) : (
      <ProductPage />
    );
    const categoryPageComponent = isMobile ? (
      <MobileCategoryPage />
    ) : (
      <CategoryPage />
    );

    return [
      createSectionRoute(Paths.Product, productPageComponent),
      createSectionRoute(Paths.MainProductGroup, categoryPageComponent),
      createSectionRoute(Paths.ProductGroup, categoryPageComponent),
      createSectionRoute(Paths.SubProductGroup, categoryPageComponent),
      createSectionRoute(Paths.OnSale, <OnSalePage />),
      createSectionRoute(Paths.NewProducts, <NewProductsPage />),
      createSectionRoute(Paths.SoldProducts, <SoldProductsPage />),
      createSectionRoute(Paths.PopularProducts, <PopularProductsPage />),
      createSectionRoute(
        Paths.RecommendedProducts,
        <RecommendedProductsPage />
      ),
    ];
  }, []);

  const sectionRoutes = getSectionRoutes(uiStore.isMobile);

  const getRoutesBehindConfig = () => {
    const routes = [];

    if (configStore.vehiclePartSearch.enabled) {
      routes.push(
        <Route
          key={Paths.VehiclePartSearch}
          exact
          path={match + Paths.VehiclePartSearch}
          children={() => overrideComponent || <VehiclePartSearchPage />}
        />
      );
    }

    return routes;
  };

  const getSectionDependantRoutes = () => {
    return sectionRoutes.map((sectionRoute) => {
      const path = `${match}${sectionRoute.path}`;

      return (
        <Route
          key={sectionRoute.path}
          exact
          path={path}
          children={() => (
            <SectionAwareRoute
              section={section}
              component={overrideComponent || sectionRoute.component}
            />
          )}
        />
      );
    });
  };

  return [...getRoutesBehindConfig(), ...getSectionDependantRoutes()];
};

export default useSectionDependantRoutes;
